import { useState, useCallback, useRef } from "react";

/**
 * useTabState Hook
 * ---------------
 * Manages tab navigation and selection state for AddressSearchPreview
 *
 * States:
 * - activeTab: Current active tab ("Address", "Owner", "Parcel")
 * - selectedAddressData: Address data from autocomplete/parcel selection
 * - suggestionSelected: Whether address form should be shown
 * - zipValidationFailed: Whether zip validation failed
 *
 * Flow:
 * 1. User selects tab → activeTab changes
 * 2. User selects address/parcel → selectedAddressData set
 * 3. Address form populates → suggestionSelected = true
 * 4. Zip validation fails → zipValidationFailed = true (overrides other states)
 */
export const useTabState = () => {
  // Tab navigation state
  const [activeTab, setActiveTab] = useState("Address");

  // Address selection state
  const [selectedAddressData, setSelectedAddressData] = useState(null);
  const [suggestionSelected, setSuggestionSelected] = useState(false);
  const [zipValidationFailed, setZipValidationFailed] = useState(false);
  const [parcelDataProcessed, setParcelDataProcessed] = useState(false);

  // Refs
  const addressTabRef = useRef(null);

  // Actions
  const switchToAddressTab = useCallback(() => {
  //  console.log('🔄 [useTabState] Switching to Address tab');
    setActiveTab("Address");
  }, []);

  const resetSelection = useCallback(() => {
  //  console.log('🔄 [useTabState] Resetting selection state');
    setSuggestionSelected(false);
    setZipValidationFailed(false);
    setSelectedAddressData(null);
    setParcelDataProcessed(false);
 //   console.log('✅ [useTabState] Selection state reset');
  }, []);

  const setAddressData = useCallback((data) => {
 //   console.log('📝 [useTabState] Setting address data:', data);
    setSelectedAddressData(data);
    setZipValidationFailed(false); // Reset validation failure on new selection
 //   console.log('✅ [useTabState] Address data set, validation reset');
  }, []);

  const showAddressForm = useCallback(() => {
  //  console.log('📝 [useTabState] Showing address form - setting suggestionSelected to true');
    setSuggestionSelected(true);
  }, []);

  const hideAddressForm = useCallback(() => {
  //  console.log('👁️ [useTabState] Hiding address form');
    setSuggestionSelected(false);
  }, []);

  const setZipValidationError = useCallback(() => {
  //  console.log('❌ [useTabState] Setting ZIP validation error');
    setZipValidationFailed(true);
    setSuggestionSelected(false); // Hide form on validation error
    setParcelDataProcessed(false);
  //  console.log('✅ [useTabState] ZIP validation error state set');
  }, []);

  const markParcelDataProcessed = useCallback(() => {
    setParcelDataProcessed(true);
  }, []);

  // Direct setter for suggestionSelected (needed by AddressTab)
  const toggleSuggestionSelected = useCallback((value) => {
    setSuggestionSelected(value);
  }, []);

  return {
    // State
    activeTab,
    selectedAddressData,
    suggestionSelected,
    zipValidationFailed,
    parcelDataProcessed,
    addressTabRef,

    // Actions
    setActiveTab,
    switchToAddressTab,
    resetSelection,
    setAddressData,
    showAddressForm,
    hideAddressForm,
    setZipValidationError,
    markParcelDataProcessed,
    setSuggestionSelected: toggleSuggestionSelected, // Direct setter for compatibility
  };
};